import React, { useState, useEffect } from "react";
import InstructionsField from "./InstructionsField";
import DragAndDropUploader from "./DragAndDropUploader";
import ModelSelectionSection from "../EditAgent/components/ModelSelectionSection";
import { SONNET_5_MODEL, defaultModelForAgentType } from "../ChatWithAgent/config/modelOptions";
import {
  createAgent,
  CreateAgentResponse,
} from "../../../services/agentService";
import {
  generatePresignedUrl,
  uploadFileToS3,
} from "../../../services/contentService";
import { useAuth } from "../../../context/AuthContext";
import { CognitoAuthService } from "../../../services/cognitoService";
import { useBrandStatus } from "../../../features/brand/hooks/useBrandStatus";
import { sanitizeFileName } from "../../../utils/fileUtils";
import { useNavigate } from "react-router-dom";
import { useCustomerWorkspace } from "../../../context/CustomerWorkspaceContext";
import TemplateReference from "../../TemplateReference";
import { AVAILABLE_TOOLS } from "../constants/agentTools";
import {
  AGENT_TYPE,
  AGENT_TYPE_OPTIONS,
  AGENT_ROLE,
  AgentRole,
  ReceptionistChannel,
  resolveAgentTypes,
} from "../../../constants/agentTypes";
import { toolsForAgentType } from "../../../constants/agentChips";
import { debug } from "../../../utils/debugLogger";
import { AgentConnectionPrompt } from "../AgentConnectionPrompt";
import { useFeatureAccess } from "../../../features/feature-access/hooks/useFeatureAccess";
import { FeatureUpgradeModal } from "../../../features/feature-access/components/FeatureUpgradeModal";

// Conversation Starter Component with Dark Theme
const ConversationStarterField: React.FC<{
  starters: Array<{
    id: string;
    suggestion: string;
  }>;
  onChange: (
    starters: Array<{
      id: string;
      suggestion: string;
    }>
  ) => void;
}> = ({ starters, onChange }) => {
  const addStarter = () => {
    const newStarter = {
      id: `starter_${Date.now()}`,
      suggestion: "",
    };
    onChange([...starters, newStarter]);
  };

  const updateStarter = (index: number, value: string) => {
    const updated = [...starters];
    updated[index] = { ...updated[index], suggestion: value };
    onChange(updated);
  };

  const removeStarter = (index: number) => {
    onChange(starters.filter((_, i) => i !== index));
  };

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <label className="block text-sm font-medium text-gray-700">
          Conversation Starters
        </label>
        <button
          type="button"
          onClick={addStarter}
          className="px-3 py-1 bg-brand-primary text-white rounded text-sm hover:bg-brand-primary-hover transition-colors"
        >
          + Add Starter
        </button>
      </div>

      <div className="space-y-4">
        {starters.map((starter, index) => (
          <div
            key={starter.id}
            className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm"
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">Starter {index + 1}</h4>
              <button
                type="button"
                onClick={() => removeStarter(index)}
                className="text-red-600 hover:text-red-700 text-sm transition-colors"
              >
                Remove
              </button>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Conversation Prompt
              </label>
              <textarea
                value={starter.suggestion}
                onChange={(e) => updateStarter(index, e.target.value)}
                placeholder="Enter a conversation starter prompt..."
                rows={3}
                className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
              />
            </div>
          </div>
        ))}
      </div>

      {starters.length === 0 && (
        <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
          <p>No conversation starters added yet.</p>
          <p className="text-sm">
            Click "Add Starter" to create conversation prompts for your agent.
          </p>
        </div>
      )}
    </div>
  );
};

// Profile Picture Component with Dark Theme
const ProfilePictureUploader: React.FC<{
  profilePicture: File | null;
  onChange: (file: File | null) => void;
}> = ({ profilePicture, onChange }) => {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file.");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB.");
        return;
      }
      onChange(file);
    }
  };

  const removePicture = () => {
    onChange(null);
  };

  return (
    <div className="mb-6">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Profile Picture
      </label>

      <div className="flex items-center gap-4">
        {profilePicture ? (
          <div className="relative">
            <img
              src={URL.createObjectURL(profilePicture)}
              alt="Profile preview"
              className="w-20 h-20 rounded-full object-cover border-2 border-gray-300"
            />
            <button
              type="button"
              onClick={removePicture}
              className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-700 transition-colors"
            >
              ×
            </button>
          </div>
        ) : (
          <div className="w-20 h-20 rounded-full bg-surface-ui flex items-center justify-center border-2 border-gray-300">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </div>
        )}

        <div>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
            id="profile-picture"
          />
          <label
            htmlFor="profile-picture"
            className="inline-block px-4 py-2 bg-white border border-gray-300 rounded cursor-pointer hover:bg-gray-50 transition-colors text-gray-700"
          >
            {profilePicture ? "Change Picture" : "Upload Picture"}
          </label>
          <p className="text-xs text-gray-500 mt-2">JPG, PNG, GIF up to 5MB</p>
        </div>
      </div>
    </div>
  );
};

const CreateAgentForm: React.FC = () => {
  const [agentName, setAgentName] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [instructions, setInstructions] = useState("");
  // Model follows the chosen role (DEFAULT_MODEL_BY_AGENT_TYPE) until the customer picks
  // one by hand; provider follows the model. Sonnet 5 is the fallback (SGL-315).
  const [model, setModel] = useState(SONNET_5_MODEL.model);
  const [provider, setProvider] = useState(SONNET_5_MODEL.provider);
  const [modelTouched, setModelTouched] = useState(false);
  const [creativity, setCreativity] = useState(0.7);
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [conversationStarters, setConversationStarters] = useState<
    Array<{
      id: string;
      suggestion: string;
    }>
  >([]);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);

  const [showTemplate, setShowTemplate] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<AgentRole | null>(null);
  const [receptionistChannel, setReceptionistChannel] = useState<ReceptionistChannel | null>(null);
  // The role decides the starting model: Opus 5 for the Visibility Manager, Sonnet 5 for
  // the rest. Stops following the role once the customer has chosen a model themselves.
  useEffect(() => {
    if (modelTouched) return;
    const agentType = resolveAgentTypes(selectedRole, receptionistChannel)[0];
    const option = defaultModelForAgentType(agentType);
    setModel(option.model);
    setProvider(option.provider);
  }, [selectedRole, receptionistChannel, modelTouched]);
  const resolvedAgentType = resolveAgentTypes(selectedRole, receptionistChannel)[0];
  // Offer only this type's tools, pre-ticked — same map (agentChips.ts) the +
  // menu's chip visibility reads, so this list can't drift from what the chat
  // window actually shows. Resets on every type change, same pattern as the
  // model default above. resolvedAgentType is undefined until a full type is
  // resolved — for Receptionist that means role AND channel both chosen (see
  // resolveAgentTypes) — so the checklist stays unfiltered/unticked until then,
  // rather than falling through to the Social Media Manager default.
  const offeredToolIds = resolvedAgentType
    ? toolsForAgentType(resolvedAgentType)
    : AVAILABLE_TOOLS.map((t) => t.id);
  useEffect(() => {
    if (!resolvedAgentType) return;
    setSelectedTools(toolsForAgentType(resolvedAgentType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedAgentType]);
  const [showVisibilityUpgradeModal, setShowVisibilityUpgradeModal] = useState(false);
  const { canAccessFeature } = useFeatureAccess();

  const { idToken, userId, user, userGroups } = useAuth();
  const navigate = useNavigate();
  const { selectedCustomerId, selectedCustomerName } = useCustomerWorkspace();
  const brandStatus = useBrandStatus(selectedCustomerId || null, idToken || null);

  // Handle tool selection
  const handleToolToggle = (toolId: string) => {
    setSelectedTools((prev) => {
      if (prev.includes(toolId)) {
        return prev.filter((id) => id !== toolId);
      } else {
        return [...prev, toolId];
      }
    });
  };

  const handleSubmit = async () => {
    setMessage(""); // Clear old messages

    if (!selectedCustomerId) {
      setMessage("No business profile selected. Please select one from the sidebar.");
      return;
    }
    if (!selectedRole) {
      setMessage("Please choose an agent type.");
      return;
    }
    if (selectedRole === AGENT_ROLE.RECEPTIONIST && !receptionistChannel) {
      setMessage("Please choose how the AI Receptionist should communicate (Email, SMS, or Both).");
      return;
    }
    if (!agentName) {
      setMessage("Please enter an agent name.");
      return;
    }
    if (!instructions) {
      setMessage("Please enter agent instructions.");
      return;
    }

    if (brandStatus === "loading" || brandStatus === "missing") {
      if (brandStatus === "loading") {
        setMessage("Verifying business profile brand details...");
      } else {
        setMessage("Please set up brand details for the selected business profile before creating an agent.");
      }
      return;
    }

    setLoading(true);
    setMessage(files.length > 0 ? "Uploading files to S3..." : "Creating agent...");

    // Track whether uploads succeeded before agent creation.
    let didUploadAnyTrainingFiles = false;

    try {
      // 1. Upload training files to S3 (optional - knowledge base is not required)
      let uploadedFiles: Array<{
        bucket: string;
        key: string;
        filename: string;
      }> = [];

      for (const file of files) {
        const user_email = CognitoAuthService.getUserEmail(idToken);
        if (!selectedCustomerId) {
          setMessage("No business profile selected. Please select one from the sidebar.");
          setLoading(false);
          return;
        }

        const sanitizedName = sanitizeFileName(file.name);
        const uploadFile = sanitizedName === file.name ? file : new File([file], sanitizedName, { type: file.type });

        setMessage(`Uploading '${uploadFile.name}' to S3...`);
        const presignedResponse = await generatePresignedUrl(
          {
            customer_id: selectedCustomerId,
            user_email,
            is_training_data: true,
            training_data_content_type: uploadFile.type,
            file_name: uploadFile.name,
            image_content_type: "",
            video_content_type: "",
            image_type: "",
            video_type: "",
            logo: false,
          },
          idToken
        );

        if (
          presignedResponse.trainingData &&
          presignedResponse.trainingData.uploadURL &&
          presignedResponse.trainingData.fileInfo
        ) {
          await uploadFileToS3(
            presignedResponse.trainingData.uploadURL,
            uploadFile,
            presignedResponse.trainingData.fileInfo.contentType
          );
          uploadedFiles.push({
            bucket: presignedResponse.trainingData.fileInfo.bucket,
            key: presignedResponse.trainingData.fileInfo.key,
            filename: presignedResponse.trainingData.fileInfo.filename,
          });
          didUploadAnyTrainingFiles = true;
        } else {
          window.alert("Failed to get presigned URL for training data upload.");
          setLoading(false);
          return;
        }
      }

      // 2. Upload profile picture if provided
      let profilePictureUrl = "";
      if (profilePicture) {
        setMessage("Uploading profile picture...");
        const user_email = CognitoAuthService.getUserEmail(idToken);
        const sanitizedProfileName = sanitizeFileName(profilePicture.name);
        const uploadProfileFile = sanitizedProfileName === profilePicture.name ? profilePicture : new File([profilePicture], sanitizedProfileName, { type: profilePicture.type });

        const presignedResponse = await generatePresignedUrl(
          {
            customer_id: selectedCustomerId,
            user_email,
            is_image: true,
            is_training_data: false,
            training_data_content_type: "",
            file_name: uploadProfileFile.name,
            image_content_type: uploadProfileFile.type,
            video_content_type: "",
            image_type: "profile",
            video_type: "",
            logo: false,
          },
          idToken
        );

        const upload = presignedResponse?.image;
        if (!upload?.uploadURL || !upload?.cloudFrontURL) {
          throw new Error(
            `Couldn't upload the picture: ${presignedResponse?.error || "no upload URL was returned"}`
          );
        }
        await uploadFileToS3(upload.uploadURL, uploadProfileFile);
        profilePictureUrl = upload.cloudFrontURL;
      }

      // 3. Create agent(s) — one per resolved backend type (AI Receptionist "both" creates two)
      const effectiveTypes = resolveAgentTypes(selectedRole, receptionistChannel);
      const baseAgentData: any = {
        agent_name: agentName,
        instructions,
        model,
        provider,
        creativity,
        customer_id: selectedCustomerId,
        profile_picture_url: profilePictureUrl,
        conversation_starters: conversationStarters,
      };
      if (uploadedFiles.length > 0) {
        baseAgentData.files = uploadedFiles;
      }
      if (selectedTools.length > 0) {
        baseAgentData.tools = selectedTools;
      }

      // Fire all creates without awaiting - agent creation happens in background
      for (const agentType of effectiveTypes) {
        createAgent({ ...baseAgentData, agent_type: agentType }, idToken).catch((error) => {
          debug.error("Agent creation API call error (may still be processing):", error);
        });
      }

      // Immediately show message and clear form
      setMessage(
        "Agent is being created in the background. You can close this window. Agent will be available at agent dashboard. Please check in 2 minutes."
      );

      // Clear the form
      setAgentName("");
      setInstructions("");
      setFiles([]);
      setProfilePicture(null);
      setConversationStarters([]);
      setSelectedTools([]);
      setSelectedRole(null);
      setReceptionistChannel(null);
    } catch (error: any) {
      debug.error("Create agent error:", error);
      // Only handle file upload errors - agent creation is fire and forget
      const errMsg =
        error instanceof Error ? error.message : "Unknown error occurred";
      if (errMsg.toLowerCase().includes("presigned")) {
        setMessage(
          "Failed to upload training files to S3 (could not get upload URL). Please try again."
        );
      } else if (errMsg.toLowerCase().includes("upload")) {
        setMessage("Failed to upload training files to S3. Please try again.");
      } else {
        setMessage(
          didUploadAnyTrainingFiles
            ? "Files were uploaded, but there was an error. Please try again."
            : "Failed to upload files. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 font-inter">
      <main className="px-10 py-10">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <div className="flex flex-wrap justify-between gap-3 p-4">
            <p className="text-gray-900 text-4xl font-black leading-tight tracking-[-0.033em] min-w-72">
              Create New Agent
            </p>
          </div>

          {/* Form Container */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            {/* Business Profile (from sidebar) */}
            <div className="mb-6 flex items-center gap-3 rounded-lg border border-theme-border bg-theme-surface px-4 py-3">
              <span className="material-icons-outlined text-[20px] text-brand-primary">business</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-theme-fg-muted uppercase tracking-wide">Business Profile</p>
                {selectedCustomerId ? (
                  <p className="text-sm font-semibold text-theme-fg truncate">
                    {selectedCustomerName ?? selectedCustomerId}
                  </p>
                ) : (
                  <p className="text-sm text-amber-600 font-medium">
                    No profile selected — use the sidebar to pick one
                  </p>
                )}
              </div>
            </div>

            {brandStatus === "missing" && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 mb-3">
                  The selected business profile does not have brand details set up. Please set up brand details or select another business profile.
                </p>
                <button
                  type="button"
                  onClick={() => navigate(`/business-profile/complete/${selectedCustomerId}`)}
                  className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors"
                >
                  Set up brand details
                </button>
              </div>
            )}

            <div
              className={brandStatus === "missing" ? "pointer-events-none opacity-60 transition-opacity" : undefined}
              aria-disabled={brandStatus === "missing"}
            >
            {/* Agent Type */}
            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-2">
                Agent Type <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-3">
                Choose the type of agent. This determines the default templates and behavior.
              </p>
              <div className="flex flex-wrap gap-3">
                {AGENT_TYPE_OPTIONS.map((option) => {
                  const isVisibilityManager = option.id === AGENT_ROLE.AI_VISIBILITY_MANAGER;
                  const isLocked = isVisibilityManager && !canAccessFeature("visibility_manager");
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => {
                        if (isLocked) {
                          setShowVisibilityUpgradeModal(true);
                          return;
                        }
                        setSelectedRole(option.id);
                        setReceptionistChannel(null);
                      }}
                      className={`text-left p-4 rounded-lg border-2 transition-all flex-1 min-w-[200px] ${
                        selectedRole === option.id
                          ? "border-[#2f4941] bg-[#2f4941]/5 shadow-sm"
                          : isLocked
                            ? "border-gray-200 bg-gray-50/50 opacity-70 hover:border-gray-300"
                            : "border-gray-200 hover:border-gray-300 bg-gray-50/50 hover:bg-gray-50"
                      }`}
                    >
                      <p className="flex items-center gap-2 font-medium text-gray-800">
                        {option.label}
                        {isLocked && (
                          <span className="rounded-full bg-[#2f4941]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#2f4941]">
                            Pro
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                    </button>
                  );
                })}
              </div>

              {/* Channel picker — shown only when AI Receptionist is selected */}
              {selectedRole === AGENT_ROLE.RECEPTIONIST && (
                <div className="mt-4 p-4 border border-[#2f4941]/20 rounded-lg bg-[#2f4941]/5">
                  <p className="text-sm font-medium text-gray-700 mb-3">
                    How should the AI Receptionist communicate? <span className="text-red-500">*</span>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {(["email", "sms", "both"] as ReceptionistChannel[]).map((ch) => (
                      <button
                        key={ch}
                        type="button"
                        onClick={() => setReceptionistChannel(ch)}
                        className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                          receptionistChannel === ch
                            ? "border-[#2f4941] bg-[#2f4941] text-white"
                            : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                        }`}
                      >
                        {ch === "email" ? "Email" : ch === "sms" ? "SMS" : "Email + SMS"}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Profile Picture */}
            <ProfilePictureUploader
              profilePicture={profilePicture}
              onChange={setProfilePicture}
            />

            {/* Agent Name */}
            <div className="mb-6">
              <label
                htmlFor="agent-name"
                className="block text-gray-700 font-medium mb-2"
              >
                Agent Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="agent-name"
                placeholder="Enter agent name..."
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              />
            </div>

            {/* Model — starts on the role's default (see DEFAULT_MODEL_BY_AGENT_TYPE);
                the customer can change it here. Provider follows the model so a GPT
                pick creates an OpenAI agent and a Claude pick a Bedrock one. */}
            <ModelSelectionSection
              selectedModel={model}
              agentType={resolvedAgentType}
              onModelChange={(id, option) => {
                setModelTouched(true);
                setModel(id);
                if (option) setProvider(option.provider);
              }}
            />

            {/* Creativity Slider */}
            <div className="mb-6">
              <label
                htmlFor="creativity-slider"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Creativity:{" "}
                <span className="font-semibold text-theme-fg">{creativity}</span>
              </label>
              <div className="flex items-center gap-4">
                <span className="text-xs text-gray-500">Low</span>
                <input
                  type="range"
                  id="creativity-slider"
                  min={0}
                  max={1}
                  step={0.01}
                  value={creativity}
                  onChange={(e) => setCreativity(Number(e.target.value))}
                  className="flex-grow h-2 rounded-lg appearance-none cursor-pointer"
                  // The track used bg-surface-ui, the same color as the card, so it was
                  // invisible. Brand fill up to the thumb, border color for the rest.
                  style={{
                    accentColor: "var(--color-brand-primary)",
                    background: `linear-gradient(to right, var(--color-brand-primary) ${creativity * 100}%, var(--color-border) ${creativity * 100}%)`,
                  }}
                />
                <span className="text-xs text-gray-500">High</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Controls how creative or varied the agent's responses are. Move
                left for more predictable answers, right for more creative ones.
              </div>
            </div>

            {/* Instructions Field */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <label
                  htmlFor="instructions"
                  className="block text-gray-700 font-medium"
                >
                  Agent Instructions
                </label>
                <button
                  type="button"
                  onClick={() => setShowTemplate(!showTemplate)}
                  className="text-brand-primary hover:text-brand-primary-dark text-sm font-medium transition-colors"
                >
                  {showTemplate ? "Hide Template" : "Show Template"}
                </button>
              </div>

              {showTemplate && (
                <TemplateReference
                  onCopyToInstructions={(template) => setInstructions(template)}
                  agentType={resolveAgentTypes(selectedRole, receptionistChannel)[0]}
                />
              )}

              <textarea
                id="instructions"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary min-h-[200px]"
                placeholder="Enter instructions for the agent..."
              />
            </div>

            {/* Tools Selection */}
            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-2">
                Tools (Optional)
              </label>
              <p className="text-sm text-gray-600 mb-3">
                Select tools to enable for this agent. Tools extend the agent's
                capabilities.
              </p>
              <div className="space-y-2 border border-gray-200 rounded-lg p-4 bg-gray-50">
                {AVAILABLE_TOOLS.filter((tool) => offeredToolIds.includes(tool.id)).map((tool) => (
                  <div
                    key={tool.id}
                    className="flex items-start space-x-3 p-2 hover:bg-surface-ui-hover rounded cursor-pointer"
                    onClick={(e) => {
                      // Don't trigger if clicking directly on the checkbox (it handles its own onChange)
                      if ((e.target as HTMLElement).tagName !== "INPUT") {
                        handleToolToggle(tool.id);
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      id={`tool-${tool.id}`}
                      checked={selectedTools.includes(tool.id)}
                      onChange={() => handleToolToggle(tool.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1 h-4 w-4 text-brand-primary focus:ring-brand-primary border-gray-300 rounded cursor-pointer"
                    />
                    <div className="flex-1">
                      <label
                        htmlFor={`tool-${tool.id}`}
                        className="text-sm font-medium text-gray-700 cursor-pointer"
                      >
                        {tool.name}
                      </label>
                      {tool.description && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {tool.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                {selectedTools.length === 0 && (
                  <p className="text-sm text-gray-500 italic">
                    No tools selected. Select tools above to enable them for
                    this agent.
                  </p>
                )}
              </div>
            </div>

            {/* Conversation Starters */}
            <ConversationStarterField
              starters={conversationStarters}
              onChange={setConversationStarters}
            />

            {/* File Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Training Files
              </label>
              <DragAndDropUploader onFilesAdded={setFiles} />
            </div>

            {/* Message Display */}
            {message && (
              <div
                className={`mb-4 px-4 py-2 rounded-lg text-sm ${
                  message.startsWith("✅") ||
                  message.includes("Agent is being created in the background")
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : message.includes("Uploading") ||
                      message.includes("Creating agent")
                    ? "bg-blue-50 text-blue-700 border border-blue-200"
                    : "bg-red-50 text-red-700 border border-red-200"
                }`}
              >
                {message}
              </div>
            )}

            {/* Connection prompts — shown once a receptionist channel is chosen */}
            {resolveAgentTypes(selectedRole, receptionistChannel).map((agentType) => (
              <AgentConnectionPrompt key={agentType} agentType={agentType} variant="inline" />
            ))}

            {/* Submit Button */}
            <div className="flex justify-end items-center pt-8">
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand-primary hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-brand-primary flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed w-32 ${
                  loading ? "cursor-not-allowed" : ""
                }`}
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        fill="currentColor"
                      ></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  "Create Agent"
                )}
              </button>
            </div>
            </div>
          </div>
        </div>
      </main>
      <FeatureUpgradeModal
        isOpen={showVisibilityUpgradeModal}
        onClose={() => setShowVisibilityUpgradeModal(false)}
        featureKey="visibility_manager"
      />
    </div>
  );
};

export default CreateAgentForm;
